/* ============================================================================
   EDICIÓN DE RUTA POR ARRASTRE

   Lo que hace posible agarrar la línea magenta entre dos puntos y estirarla
   hasta donde uno quiere pasar: al soltar, ahí queda un waypoint nuevo y la
   ruta se dobla. Es la forma natural de esquivar una montaña o rodear un
   espacio aéreo sin tener que calcular coordenadas.

   Todo acá es puro y trabaja en PÍXELES de pantalla, no en grados. Dos
   razones:

     1. La cercanía que importa es la del dedo al trazo, y eso se mide en la
        pantalla. A un grado de longitud le caben muchos píxeles en Lima y
        muy pocos cerca del polo; con una tolerancia en grados, la línea sería
        fácil de agarrar en un lado del mapa e imposible en el otro.

     2. Separa la geometría de Leaflet. Quien llama proyecta a píxeles y pasa
        números; así esto se puede probar sin navegador, que es justamente
        donde se encontraron los errores de este módulo.

   La conversión de vuelta a latitud y longitud la hace el mapa, que es el
   único que sabe de proyecciones.
   ========================================================================== */

/**
 * Proyecta el punto `p` sobre el segmento `a`–`b`.
 *
 * Devuelve el punto del segmento más cercano a `p`, su posición relativa `t`
 * (0 en `a`, 1 en `b`) y la distancia. `t` se recorta a [0, 1]: sin eso, un
 * cursor pasado de largo proyectaría FUERA del tramo, y la ruta se doblaría
 * hacia un punto por el que no pasa ninguna línea.
 */
export function proyectarEnSegmento(p, a, b) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const largo2 = dx * dx + dy * dy;

  // Segmento degenerado: dos waypoints en la misma coordenada. Pasa de verdad
  // —un vuelo local tiene origen y destino iguales— y sin este caso la
  // división daría NaN, que se propaga en silencio hasta un atributo del SVG.
  if (largo2 === 0) {
    return { x: a.x, y: a.y, t: 0, distancia: Math.hypot(p.x - a.x, p.y - a.y) };
  }

  const bruto = ((p.x - a.x) * dx + (p.y - a.y) * dy) / largo2;
  const t = Math.min(1, Math.max(0, bruto));
  const x = a.x + t * dx;
  const y = a.y + t * dy;

  return { x, y, t, distancia: Math.hypot(p.x - x, p.y - y) };
}

/**
 * ¿Sobre qué tramo de la ruta está el puntero?
 *
 * @param {{x:number,y:number}} punto     posición del cursor o del dedo, en píxeles
 * @param {Array<{id:*,x:number,y:number}>} vertices  waypoints CON coordenadas, en píxeles y en orden
 * @param {object} [opciones]
 * @param {number} [opciones.tolerancia]  radio de agarre en píxeles
 * @param {number} [opciones.margenVertice] distancia a un waypoint existente dentro de la cual NO se agarra
 * @returns {{idAnterior:*, indice:number, x:number, y:number, t:number, distancia:number}|null}
 */
export function tramoBajoElPuntero(punto, vertices = [], opciones = {}) {
  const { tolerancia = 14, margenVertice = 12 } = opciones;

  if (!Number.isFinite(punto?.x) || !Number.isFinite(punto?.y)) return null;
  const utiles = vertices.filter((v) => Number.isFinite(v?.x) && Number.isFinite(v?.y));
  if (utiles.length < 2) return null;

  let mejor = null;

  for (let i = 0; i < utiles.length - 1; i += 1) {
    const a = utiles[i];
    const b = utiles[i + 1];
    const proyeccion = proyectarEnSegmento(punto, a, b);

    /* Cerca de un waypoint que ya existe no se agarra la línea: ahí lo que el
       piloto quiere es mover ESE punto, no crear otro pegado. Sin esta regla,
       un pulso al soltar deja dos waypoints a tres píxeles y la ruta queda con
       un tramo de 0,1 NM que ensucia el perfil y el combustible.

       El margen se limita a un cuarto del tramo. Antes era absoluto y se
       aplicaba a TODOS los vértices: con el mapa alejado, donde un tramo mide
       veinte píxeles, los márgenes de sus dos extremos se tocaban y la línea
       entera quedaba inagarrable — sin ningún aviso, como si la función no
       existiera. */
    const largo = Math.hypot(b.x - a.x, b.y - a.y);
    const margen = Math.min(margenVertice, largo / 4);
    const aVertice = Math.min(
      Math.hypot(punto.x - a.x, punto.y - a.y),
      Math.hypot(punto.x - b.x, punto.y - b.y),
    );
    if (aVertice <= margen) continue;
    // Estrictamente menor: con una ruta que se cruza a sí misma, empatan dos
    // tramos y gana el primero, que es el más cercano al origen. Elegir
    // siempre el mismo hace que la interacción sea repetible.
    if (!mejor || proyeccion.distancia < mejor.distancia) {
      mejor = { idAnterior: a.id, indice: i, ...proyeccion };
    }
  }

  return mejor && mejor.distancia <= tolerancia ? mejor : null;
}

/* ----------------------------------------------------------------------------
   Identificadores

   El código anterior usaba `Date.now()` para el id de cada waypoint nuevo.
   Con el arrastre eso se rompe: dos inserciones en el mismo milisegundo —muy
   posible al doblar la ruta dos veces seguidas— daban el MISMO id. React
   entonces reusa el nodo de uno para el otro, y el segundo punto aparece con
   el nombre del primero sin que nada avise.
   -------------------------------------------------------------------------- */
let contadorId = 0;

/** Identificador único de waypoint. Nunca repite, ni dentro del mismo milisegundo. */
export function nuevoIdPunto() {
  contadorId += 1;
  return `wp_${Date.now().toString(36)}_${contadorId.toString(36)}`;
}

/** Nombre por defecto de un punto creado sobre el mapa. */
export function nombrePorCoordenada(lat, lon) {
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return "PTO";
  return `PTO ${lat.toFixed(2)},${lon.toFixed(2)}`;
}

/**
 * ¿Se puede arrastrar este waypoint?
 *
 * Origen y destino no: los fija el piloto eligiendo un aeródromo, con su
 * elevación y su nombre OACI. Arrastrarlos los convertiría en una coordenada
 * cualquiera con el nombre del aeropuerto todavía puesto, que es peor que no
 * poder moverlos. Para cambiarlos está "Fijar origen / Fijar destino".
 */
export function esArrastrable(waypoint) {
  return Boolean(waypoint) && waypoint.id !== "origin" && waypoint.id !== "destination";
}

/**
 * Inserta un punto nuevo justo después de `idAnterior`.
 *
 * Devuelve la MISMA lista si no hay dónde insertar, para que quien llama
 * pueda comparar por identidad y no re-renderizar de gusto.
 */
export function insertarDespues(waypoints = [], idAnterior, nuevo) {
  const indice = waypoints.findIndex((w) => w.id === idAnterior);
  if (indice === -1) return waypoints;
  if (!Number.isFinite(nuevo?.lat) || !Number.isFinite(nuevo?.lon)) return waypoints;

  const punto = {
    id: nuevo.id ?? nuevoIdPunto(),
    name: nuevo.name || nombrePorCoordenada(nuevo.lat, nuevo.lon),
    lat: nuevo.lat,
    lon: nuevo.lon,
    // Sin elevación medida se guarda null, nunca un número inventado: un dato
    // sintético se ve igual que uno real y después nadie sabe cuál era cuál.
    elevFt: Number.isFinite(nuevo.elevFt) ? nuevo.elevFt : null,
    tempC: Number.isFinite(nuevo.tempC) ? nuevo.tempC : 15,
    legNm: 20,
  };

  return [...waypoints.slice(0, indice + 1), punto, ...waypoints.slice(indice + 1)];
}

/**
 * Mueve un punto ya existente.
 *
 * La elevación se borra siempre que cambian las coordenadas. Es el error más
 * fácil de cometer acá: arrastrar el punto 20 NM al costado y dejarle los
 * 9 800 ft del lugar anterior. El perfil vertical seguiría dibujándose, con
 * un número que ya no corresponde a ningún terreno.
 */
export function moverPunto(waypoints = [], id, lat, lon) {
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return waypoints;
  const indice = waypoints.findIndex((w) => w.id === id);
  if (indice === -1) return waypoints;
  if (!esArrastrable(waypoints[indice])) return waypoints;

  const anterior = waypoints[indice];
  const seMovio = anterior.lat !== lat || anterior.lon !== lon;

  return waypoints.map((w, i) => (
    i === indice
      ? {
        ...w,
        lat,
        lon,
        elevFt: seMovio ? null : w.elevFt,
        name: nombreSigueValiendo(w) ? w.name : nombrePorCoordenada(lat, lon),
      }
      : w
  ));
}

/**
 * Un nombre puesto por el piloto o tomado de un aeródromo se conserva al
 * mover el punto; uno generado a partir de las coordenadas, no — quedaría
 * diciendo un lugar donde el punto ya no está.
 */
function nombreSigueValiendo(waypoint) {
  return typeof waypoint?.name === "string" && !/^PTO\s-?\d/.test(waypoint.name);
}

/** Aplica cambios sueltos a un punto (por ejemplo, la elevación que llegó después). */
export function actualizarPunto(waypoints = [], id, cambios = {}) {
  const indice = waypoints.findIndex((w) => w.id === id);
  if (indice === -1) return waypoints;
  return waypoints.map((w, i) => (i === indice ? { ...w, ...cambios } : w));
}

/* ----------------------------------------------------------------------------
   Normalizar la ruta

   La aplicación entera da por sentado que `waypoints` es un arreglo con al
   menos dos elementos, el primero con id "origin" y el último con
   "destination". Está escrito así en decenas de lugares —`waypoints[0]`,
   `waypoints[waypoints.length - 1]`— y es razonable: es la forma de un vuelo.

   El problema es que ese supuesto no estaba garantizado en ninguna parte. Una
   ruta guardada en un formato viejo, un `setWaypoints` con un valor
   inesperado, una lista que quedó en cero: cualquiera de esas cosas deja
   `waypoints` sin lo que todos esperan, y la app se cae entera con
   "undefined is not an object (evaluating 'waypoints[0]')" — una pantalla de
   error en lugar de la aplicación, sin forma de volver.

   Esta función es el portero. Todo lo que entre al estado pasa por acá, así
   que el supuesto deja de ser una esperanza y pasa a ser un hecho.
   -------------------------------------------------------------------------- */

/** Casillero vacío de extremo, el que se ve antes de elegir aeródromo. */
export function puntoVacio(id) {
  return {
    id,
    name: id === "origin" ? "PUNTO DE DESPEGUE (sin definir)" : "PUNTO DE ATERRIZAJE (sin definir)",
    lat: null,
    lon: null,
    elevFt: 0,
    tempC: 15,
    legNm: id === "origin" ? 0 : 20,
  };
}

/**
 * Devuelve una ruta que cumple los supuestos de toda la aplicación, venga lo
 * que venga: `undefined`, un objeto suelto, una lista de un elemento, ids
 * repetidos, coordenadas que no son números.
 *
 * Nunca lanza. Un dato roto no puede costar la pantalla entera: en el peor
 * caso se vuelve a la ruta en blanco, que es un estado del que se puede salir.
 */
export function normalizarRuta(entrada) {
  const lista = Array.isArray(entrada)
    ? entrada.filter((w) => w && typeof w === "object")
    : [];

  const saneado = lista.map((w) => {
    // Una coordenada que no es un número finito vale lo mismo que no tenerla,
    // y el resto de la app ya sabe tratar el null. NaN, en cambio, se propaga
    // hasta un atributo del SVG y el mapa desaparece sin decir nada.
    const lat = Number.isFinite(w.lat) ? w.lat : null;
    const lon = Number.isFinite(w.lon) ? w.lon : null;
    // La elevación ausente se deja ausente: convertir `undefined` en `null`
    // no arregla nada y obligaría a reescribir el punto entero, perdiendo su
    // identidad. Solo se corrige un número que no es número.
    const elevFt = w.elevFt === undefined ? undefined : (Number.isFinite(w.elevFt) ? w.elevFt : null);
    // Se devuelve el MISMO objeto si no hubo nada que corregir. Ver la nota
    // sobre identidad al final de la función.
    return (lat === w.lat && lon === w.lon && elevFt === w.elevFt) ? w : { ...w, lat, lon, elevFt };
  });

  // Menos de dos puntos no es una ruta. Se completa con casilleros vacíos en
  // vez de dejar que reviente el primer `waypoints[1]`.
  if (saneado.length === 0) return [puntoVacio("origin"), puntoVacio("destination")];
  if (saneado.length === 1) {
    const unico = saneado[0];
    return unico.id === "destination"
      ? [puntoVacio("origin"), unico]
      : [{ ...unico, id: "origin" }, puntoVacio("destination")];
  }

  const salida = [...saneado];
  if (salida[0].id !== "origin") salida[0] = { ...salida[0], id: "origin" };
  const ultimo = salida.length - 1;
  if (salida[ultimo].id !== "destination") salida[ultimo] = { ...salida[ultimo], id: "destination" };

  // Ids repetidos: React reusa el nodo de uno para el otro y los puntos se
  // mezclan en pantalla. Se renombran los intermedios, nunca los extremos.
  const vistos = new Set();
  for (let i = 0; i < salida.length; i += 1) {
    const esExtremo = i === 0 || i === ultimo;
    if (salida[i].id == null || (vistos.has(salida[i].id) && !esExtremo)) {
      salida[i] = { ...salida[i], id: nuevoIdPunto() };
    }
    vistos.add(salida[i].id);
  }

  /* IDENTIDAD — importa más de lo que parece.
     Esto corre en CADA escritura del estado. Si devolviera siempre un arreglo
     nuevo, React vería un cambio aunque no lo hubiera, y cualquier efecto que
     dependa de la ruta y a su vez la escriba entraría en un bucle infinito: la
     app se congela sin ningún error en la consola. Cuando no hubo nada que
     corregir se devuelve la entrada tal cual. */
  const sinCambios = Array.isArray(entrada)
    && entrada.length === salida.length
    && salida.every((w, i) => w === entrada[i]);

  return sinCambios ? entrada : salida;
}

/**
 * ¿Esta ruta cumple los supuestos? Sirve para comprobarlo en las pruebas sin
 * repetir la lista de condiciones en cada una.
 */
export function rutaValida(ruta) {
  return Array.isArray(ruta)
    && ruta.length >= 2
    && ruta[0]?.id === "origin"
    && ruta.at(-1)?.id === "destination"
    && new Set(ruta.map((w) => w.id)).size === ruta.length
    && ruta.every((w) => w && (w.lat === null || Number.isFinite(w.lat)))
    && ruta.every((w) => w && (w.lon === null || Number.isFinite(w.lon)));
}

/**
 * Los dos tramos que reemplazarían al tramo agarrado, para dibujar la vista
 * previa punteada mientras se arrastra. Sin esto no se ve hacia dónde va a
 * doblar la ruta hasta soltar, y hay que soltar para enterarse.
 */
export function vistaPreviaDeArrastre(vertices = [], indice, punto) {
  const utiles = vertices.filter((v) => Number.isFinite(v?.x) && Number.isFinite(v?.y));
  const a = utiles[indice];
  const b = utiles[indice + 1];
  if (!a || !b || !Number.isFinite(punto?.x) || !Number.isFinite(punto?.y)) return null;
  return [[a, punto], [punto, b]];
}

/* ----------------------------------------------------------------------------
   RED DE SEGURIDAD: una ruta no pierde coordenadas por accidente

   Síntoma reportado cinco veces: se arrastra la línea, se suelta, y la ruta se
   vacía. El cartel de diagnóstico terminó señalando el motivo — el origen se
   quedaba sin latitud ni longitud, y con un solo punto no hay línea que
   dibujar.

   Borrar la ruta, cargar otra, o editar las coordenadas a mano SON cosas que
   el piloto pide, y esas pasan marcadas como intencionales. Lo que no puede
   pasar es que un punto que ya tenía posición la pierda sola: eso siempre es
   un error, y en una aplicación de vuelo cuesta un plan de vuelo entero.

   Ante la duda se conserva lo que había. Perder una coordenada es mucho más
   caro que conservar una de más, que el piloto puede corregir viéndola.
   -------------------------------------------------------------------------- */

/**
 * @param {Array} anterior  la ruta que estaba
 * @param {Array} siguiente la ruta propuesta
 * @param {object} [opciones]
 * @param {boolean} [opciones.intencional] true cuando el piloto pidió el cambio
 * @returns {{ruta:Array, restaurados:Array}}
 */
export function preservarCoordenadas(anterior, siguiente, { intencional = false } = {}) {
  if (intencional || !Array.isArray(anterior) || !Array.isArray(siguiente)) {
    return { ruta: siguiente, restaurados: [] };
  }

  const previos = new Map(anterior.map((w) => [w?.id, w]));
  const restaurados = [];

  const ruta = siguiente.map((w) => {
    const antes = previos.get(w?.id);
    if (!antes) return w;                       // punto nuevo: nada que conservar
    const teniaPosicion = Number.isFinite(antes.lat) && Number.isFinite(antes.lon);
    const sigueTeniendo = Number.isFinite(w.lat) && Number.isFinite(w.lon);
    if (!teniaPosicion || sigueTeniendo) return w;
    restaurados.push(w.id);
    return { ...w, lat: antes.lat, lon: antes.lon, elevFt: antes.elevFt, name: antes.name };
  });

  return { ruta: restaurados.length ? ruta : siguiente, restaurados };
}

/**
 * Inserta un punto en el tramo geográficamente más cercano.
 *
 * Es la salida de emergencia de `insertarDespues`. Esa función necesita el id
 * del punto anterior, y si por lo que sea ese id no está en la ruta —porque la
 * lista cambió entre que se agarró la línea y que se soltó— devuelve la ruta
 * intacta: el punto se pierde en silencio y el piloto ve que la línea "vuelve"
 * a donde estaba, sin ninguna explicación.
 *
 * Acá no hace falta ningún id: se busca sobre qué tramo cae el punto y se
 * mete ahí. Trabaja en grados, no en píxeles, porque no hay pantalla de por
 * medio; para tramos de ruta a esta escala la distorsión no cambia cuál es el
 * tramo más cercano.
 */
export function insertarEnTramoMasCercano(waypoints = [], nuevo = {}) {
  if (!Number.isFinite(nuevo.lat) || !Number.isFinite(nuevo.lon)) return waypoints;

  const conPosicion = waypoints
    .map((w, i) => ({ w, i }))
    .filter(({ w }) => Number.isFinite(w.lat) && Number.isFinite(w.lon));
  if (conPosicion.length < 2) return waypoints;

  let mejor = null;
  for (let k = 0; k < conPosicion.length - 1; k += 1) {
    const a = conPosicion[k].w;
    const b = conPosicion[k + 1].w;
    const { distancia } = proyectarEnSegmento(
      { x: nuevo.lon, y: nuevo.lat },
      { x: a.lon, y: a.lat },
      { x: b.lon, y: b.lat },
    );
    if (!mejor || distancia < mejor.distancia) mejor = { distancia, idAnterior: a.id };
  }

  return mejor ? insertarDespues(waypoints, mejor.idAnterior, nuevo) : waypoints;
}
